'use client'

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useLocale } from 'next-intl'
import { useCallback, useEffect, useRef, useState } from 'react'

import {
  getPushSettings,
  type PushSettingsResponse
} from '../api/notificationsApi'
import {
  getBrowserPushSubscription,
  hasApplicationServerKey,
  isWebPushSupported
} from '../lib/pushSubscription'
import {
  registerThisBrowser,
  rememberPushUser,
  WebPushError,
  withPushLock
} from '../lib/webPush'
import { notificationsQueryKeys } from '../notifications.constants'
import { useNotificationsUserId } from './useNotificationsUserId'

export { isWebPushSupported } from '../lib/pushSubscription'

export function usePushSettings(enabled = true) {
  const userId = useNotificationsUserId()

  return useQuery({
    queryKey: notificationsQueryKeys.push(userId ?? ''),
    enabled: enabled && Boolean(userId) && isWebPushSupported(),
    queryFn: getPushSettings
  })
}

/**
 * State of Web Push in this browser for the signed-in account (BAT-989):
 *
 * - `unsupported` — no Web Push in the browser, or switched off on the server;
 * - `default` — permission never asked;
 * - `denied` — blocked in the browser settings;
 * - `subscribing` — connection in progress;
 * - `subscribed` — this browser is registered for this account;
 * - `not-subscribed` — permission granted, but this browser is not
 *   registered for this account;
 * - `error` — connecting or checking failed.
 */
export type WebPushBrowserStatus =
  | 'unsupported'
  | 'default'
  | 'denied'
  | 'subscribing'
  | 'subscribed'
  | 'not-subscribed'
  | 'error'

type CheckedStatus = Exclude<WebPushBrowserStatus, 'subscribing' | 'error'>

export interface WebPushBrowserState {
  status: WebPushBrowserStatus
  /** True until the browser state has been read once. */
  isLoading: boolean
  /**
   * Connects this browser; call it from a click — it may show the native
   * prompt. Resolves with the resulting status, never throws. Concurrent calls
   * share one attempt.
   */
  subscribe: () => Promise<WebPushBrowserStatus>
  /** Reads the permission and the registration again. */
  recheck: () => Promise<void>
}

async function inspectThisBrowser(
  push: PushSettingsResponse | undefined
): Promise<CheckedStatus | undefined> {
  if (!isWebPushSupported()) {
    return 'unsupported'
  }
  if (push && !push.vapid_public_key) {
    return 'unsupported'
  }
  if (Notification.permission === 'denied') {
    return 'denied'
  }
  if (Notification.permission !== 'granted') {
    return 'default'
  }
  if (!push) {
    return undefined
  }

  const subscription = await getBrowserPushSubscription()
  const registered =
    subscription !== null &&
    hasApplicationServerKey(subscription, push.vapid_public_key) &&
    (push.subscriptions ?? []).some(
      item => item.endpoint === subscription.endpoint
    )

  return registered ? 'subscribed' : 'not-subscribed'
}

export function useWebPushBrowserState(): WebPushBrowserState {
  const queryClient = useQueryClient()
  const userId = useNotificationsUserId()
  const language = useLocale()
  const push = usePushSettings()
  const { refetch } = push

  const [checked, setChecked] = useState<CheckedStatus>()
  const [phase, setPhase] = useState<'idle' | 'subscribing' | 'error'>('idle')
  const inFlight = useRef<Promise<WebPushBrowserStatus> | undefined>(undefined)

  const cachedPush = useCallback(
    () =>
      userId
        ? queryClient.getQueryData<PushSettingsResponse>(
            notificationsQueryKeys.push(userId)
          )
        : undefined,
    [queryClient, userId]
  )

  const evaluate = useCallback(
    async (data?: PushSettingsResponse) => {
      const next = await inspectThisBrowser(data ?? cachedPush())
      if (next) {
        setChecked(next)
      }
    },
    [cachedPush]
  )

  useEffect(() => {
    if (!inFlight.current) {
      void evaluate(push.data)
    }
  }, [evaluate, userId, push.data])

  // The fan may change the permission in the browser's site settings while
  // the page is open.
  useEffect(() => {
    if (!isWebPushSupported() || !navigator.permissions?.query) {
      return
    }

    let cancelled = false
    let permission: PermissionStatus | undefined
    const onChange = () => {
      setPhase('idle')
      void evaluate()
    }

    navigator.permissions
      .query({ name: 'notifications' })
      .then(status => {
        if (cancelled) {
          return
        }
        permission = status
        status.addEventListener('change', onChange)
      })
      .catch(() => undefined)

    return () => {
      cancelled = true
      permission?.removeEventListener('change', onChange)
    }
  }, [evaluate])

  const subscribe = useCallback((): Promise<WebPushBrowserStatus> => {
    if (inFlight.current) {
      return inFlight.current
    }
    if (!isWebPushSupported()) {
      setChecked('unsupported')
      return Promise.resolve('unsupported')
    }

    // The native prompt has to come straight from the click: nothing may be
    // awaited before it, or browsers treat it as unprompted and suppress it.
    const asked =
      Notification.permission === 'default'
        ? Notification.requestPermission()
        : Promise.resolve(Notification.permission)

    const settle = (status: CheckedStatus) => {
      setChecked(status)
      setPhase('idle')
      return status
    }

    const run = (async (): Promise<WebPushBrowserStatus> => {
      setPhase('subscribing')
      try {
        const permission = await asked
        if (permission !== 'granted') {
          return settle(permission === 'denied' ? 'denied' : 'default')
        }
        if (!userId) {
          throw new Error('web push: no session')
        }

        const key = notificationsQueryKeys.push(userId)
        const settings =
          cachedPush() ??
          (await queryClient.fetchQuery({
            queryKey: key,
            queryFn: getPushSettings
          }))
        const vapidPublicKey = settings?.vapid_public_key
        if (!vapidPublicKey) {
          return settle('unsupported')
        }

        const subscription = await withPushLock(() =>
          registerThisBrowser({ vapidPublicKey, language })
        )
        rememberPushUser(userId)

        queryClient.setQueryData<PushSettingsResponse>(key, current =>
          current
            ? {
                ...current,
                subscriptions: [
                  ...(current.subscriptions ?? []).filter(
                    item => item.endpoint !== subscription.endpoint
                  ),
                  {
                    endpoint: subscription.endpoint,
                    language,
                    created_at: new Date().toISOString()
                  }
                ]
              }
            : current
        )

        return settle('subscribed')
      } catch (error) {
        if (error instanceof WebPushError) {
          return settle(error.reason)
        }
        setPhase('error')
        return 'error'
      } finally {
        inFlight.current = undefined
      }
    })()

    inFlight.current = run
    return run
  }, [cachedPush, language, queryClient, userId])

  const recheck = useCallback(async () => {
    setPhase('idle')
    const data =
      userId && isWebPushSupported() ? (await refetch()).data : undefined
    await evaluate(data)
  }, [evaluate, refetch, userId])

  let status: WebPushBrowserStatus
  if (phase !== 'idle') {
    status = phase
  } else if (checked) {
    status = checked
  } else {
    status = push.isError ? 'error' : 'default'
  }

  return {
    status,
    isLoading: checked === undefined && phase === 'idle' && !push.isError,
    subscribe,
    recheck
  }
}
